/**
 * Client for GET /api/cognitory/v2/platform-admin/systems/:systemId/members.
 */

import {
  buildCognitoryPlatformAdminMembersPath,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";

export type PlatformAdministratorMemberRow = {
  email: string | null;
  displayName: string | null;
  role: "owner" | "editor" | "viewer";
  status: "active" | "revoked";
  createdAt: string;
  accountState: "available" | "disabled" | "unverified" | "missing";
};

export type PlatformAdministratorMembersClientResult =
  | {
      kind: "listed";
      requestId: string;
      systemId: string;
      members: PlatformAdministratorMemberRow[];
      truncated: boolean;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "invalid_request"
        | "temporary_failure"
        | "unable_to_verify";
      requestId: string | null;
    };

const TOP_LEVEL_KEYS = new Set([
  "decision",
  "requestId",
  "systemId",
  "members",
  "truncated",
]);

const MEMBER_KEYS = new Set([
  "email",
  "displayName",
  "role",
  "status",
  "createdAt",
  "accountState",
]);

function isMemberRow(value: unknown): value is PlatformAdministratorMemberRow {
  if (!value || typeof value !== "object") {
    return false;
  }
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length !== MEMBER_KEYS.size) {
    return false;
  }
  for (const key of keys) {
    if (!MEMBER_KEYS.has(key)) {
      return false;
    }
  }
  const email = record.email;
  if (email !== null && typeof email !== "string") {
    return false;
  }
  const displayName = record.displayName;
  if (displayName !== null && typeof displayName !== "string") {
    return false;
  }
  if (
    record.role !== "owner" &&
    record.role !== "editor" &&
    record.role !== "viewer"
  ) {
    return false;
  }
  if (record.status !== "active" && record.status !== "revoked") {
    return false;
  }
  if (typeof record.createdAt !== "string" || record.createdAt.length === 0) {
    return false;
  }
  if (
    record.accountState !== "available" &&
    record.accountState !== "disabled" &&
    record.accountState !== "unverified" &&
    record.accountState !== "missing"
  ) {
    return false;
  }
  return true;
}

function parseListedRequestId(body: unknown): string | null {
  if (!body || typeof body !== "object") {
    return null;
  }
  const requestId = (body as { requestId?: unknown }).requestId;
  if (typeof requestId !== "string" || requestId.trim().length === 0) {
    return null;
  }
  return requestId;
}

export async function fetchPlatformAdministratorMembers(input: {
  systemId: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<PlatformAdministratorMembersClientResult> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }

  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  let response: Response;
  try {
    response = await fetchImpl(
      `${base}${buildCognitoryPlatformAdminMembersPath(input.systemId)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        cache: "no-store",
      },
    );
  } catch {
    return { kind: "temporary_failure", requestId: null };
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const requestId =
    body &&
    typeof body === "object" &&
    typeof (body as { requestId?: unknown }).requestId === "string"
      ? (body as { requestId: string }).requestId
      : null;

  if (response.status === 401) {
    return { kind: "unauthenticated", requestId };
  }
  if (response.status === 403) {
    return { kind: "forbidden", requestId };
  }
  if (response.status === 400) {
    return { kind: "invalid_request", requestId };
  }
  if (response.status === 503) {
    return { kind: "temporary_failure", requestId };
  }
  if (response.status !== 200 || !body || typeof body !== "object") {
    return { kind: "unable_to_verify", requestId };
  }

  const record = body as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length !== TOP_LEVEL_KEYS.size) {
    return { kind: "unable_to_verify", requestId };
  }
  for (const key of keys) {
    if (!TOP_LEVEL_KEYS.has(key)) {
      return { kind: "unable_to_verify", requestId };
    }
  }

  if (record.decision !== "platform_administrator_members_listed") {
    return { kind: "unable_to_verify", requestId };
  }
  if (typeof record.systemId !== "string" || record.systemId.length === 0) {
    return { kind: "unable_to_verify", requestId };
  }
  if (typeof record.truncated !== "boolean") {
    return { kind: "unable_to_verify", requestId };
  }
  if (!Array.isArray(record.members)) {
    return { kind: "unable_to_verify", requestId };
  }

  const members: PlatformAdministratorMemberRow[] = [];
  for (const item of record.members) {
    if (!isMemberRow(item)) {
      return { kind: "unable_to_verify", requestId };
    }
    members.push({
      email: item.email,
      displayName: item.displayName,
      role: item.role,
      status: item.status,
      createdAt: item.createdAt,
      accountState: item.accountState,
    });
  }

  const listedRequestId = parseListedRequestId(body);
  if (!listedRequestId) {
    return { kind: "unable_to_verify", requestId: null };
  }

  return {
    kind: "listed",
    requestId: listedRequestId,
    systemId: record.systemId,
    members,
    truncated: record.truncated,
  };
}
